import React, { useState, useEffect } from 'react';
import { string, func, oneOfType } from 'prop-types';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureUser, ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';

import { AvatarLarge, NamedLink, InlineTextButton } from '../../components';
import PhoneNumberModal from '../PhoneNumberModal/PhoneNumberModal';
import locationicon from './assets/LocationIcon.png';

import css from './UserCard.module.css';
import CircleImage from '../CircleImage/CircleImage';

// Approximated collapsed size so that there are ~three lines of text
// in the desktop layout in the author section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 170;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio, dubaiListing } = props;
  const truncatedBio = truncated(bio);

  const handleShowMoreClick = () => {
    setExpand(true);
  };
  const showMore = (
    <InlineTextButton rootClassName={css.showMore} onClick={handleShowMoreClick}>
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bio : truncatedBio}
      {bio !== truncatedBio && !expand ? showMore : null}
    </p>
  );
};

ExpandableBio.defaultProps = { className: null };

ExpandableBio.propTypes = {
  className: string,
  bio: string.isRequired,
};

const UserCard = props => {
  const {
    rootClassName,
    className,
    user,
    currentUser,
    onContactUser,
    sellerDetail,
    listingAddress,
    isShippingOnly,
    submitGuestPhoneNumber,
    intl,
    dubaiListing,
    famousListing,
  } = props;

  const [disableContent, setDisableContent] = useState(false);
  const [isPhoneModalOpen, setPhoneModalOpen] = useState(false);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  let { displayName, bio } = ensuredUser.attributes.profile;
  bio = dubaiListing
    ? 'We are a chocolatier specializing in luxurious Dubai-inspired confections, featuring creamy pistachio fillings, crispy knafeh layers, and rich chocolate coatings. Our creations blend traditional Middle Eastern flavors with modern indulgence.'
    : bio;

  let firstName = displayName.split(' ')[0];
  const trackEvent = (eventName, eventData, fbEventName, fbEventData) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventData);
    } else {
      setTimeout(() => trackEvent(eventName, eventData, fbEventName, fbEventData), 200);
    }
    if (window.fbq) {
      window.fbq('trackCustom', fbEventName, fbEventData);
    } else {
      setTimeout(() => trackEvent(eventName, eventData, fbEventName, fbEventData), 200);
    }
  };

  // Check if user is a guest without a phone number
  const isGuestWithoutPhone =
    currentUser?.attributes?.profile?.protectedData?.isGuest === true &&
    !currentUser?.attributes?.profile?.protectedData?.phoneNumber;

  const handleContactUserClick = () => {
    // If user is a guest without a phone number, show the phone modal
    if (isGuestWithoutPhone) {
      setPhoneModalOpen(true);
    } else {
      // Otherwise, proceed with contact
      onContactUser(user);
      trackEvent(
        'message_seller_link_clicked',
        { event_category: 'Button', event_label: 'Message seller link clicked' },
        'message_seller_link_clicked',
        { content_type: 'Button', affiliation: 'Message seller link clicked' }
      );
    }
  };

  const handlePhoneModalClose = () => {
    setPhoneModalOpen(false);
  };

  const handlePhoneSubmit = phone => {
    submitGuestPhoneNumber(phone);
    setPhoneModalOpen(false);

    // After submitting phone, proceed with contact
    onContactUser(user);
    trackEvent(
      'message_seller_link_clicked',
      { event_category: 'Button', event_label: 'Message seller link clicked' },
      'message_seller_link_clicked',
      { content_type: 'Button', affiliation: 'Message seller link clicked' }
    );
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const separator = isCurrentUser ? null : <span className={css.linkSeparator}>•</span>;

  const contact = (
    <>
      {isPhoneModalOpen && (
        <PhoneNumberModal
          onClose={handlePhoneModalClose}
          onConfirm={handlePhoneSubmit}
          intl={intl}
        />
      )}
      <InlineTextButton
        rootClassName={css.contact}
        onClick={handleContactUserClick}
        enforcePagePreloadFor="SignupPage"
      >
        <FormattedMessage id="UserCard.contactUser" values={{ name: firstName }} />
      </InlineTextButton>
    </>
  );

  const editProfileMobile = (
    <div className={css.editProfileMobile}>
      <NamedLink className={css.editButton} name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </div>
  );

  const editProfileDesktop = (
    <NamedLink className={css.editButton} name="ProfileSettingsPage">
      <FormattedMessage id="ListingPage.editProfileLink" />
    </NamedLink>
  );

  const links = ensuredUser.id ? (
    <>
      <p className={linkClasses}>
        {!disableContent && (
          <NamedLink className={css.link} name="ProfilePage" params={{ id: ensuredUser.id.uuid }}>
            <FormattedMessage id="UserCard.viewProfileLink" />
          </NamedLink>
        )}
      </p>

      {!sellerDetail && (
        <div className={css.actionButtons}>
          {!sellerDetail ? (
            isCurrentUser ? (
              <>
                <div className={css.mobileOnlyButton}>{editProfileMobile}</div>
                <div className={css.desktopOnlyButton}>{editProfileDesktop}</div>
              </>
            ) : (
              contact
            )
          ) : null}
        </div>
      )}
    </>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.content}>
        {dubaiListing ? null : !disableContent ? (
          <AvatarLarge className={css.avatar} user={user} />
        ) : (
          <AvatarLarge className={css.avatarDisabled} user={user} />
        )}

        {dubaiListing && <CircleImage className={css.avatarDisabled} />}
        <div className={css.info}>
          {!dubaiListing && (
            <div className={css.headingRow}>
              <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
            </div>
          )}
          {isShippingOnly || famousListing ? null : listingAddress ? (
            <div className={css.locationField}>
              <p>
                <img
                  src={locationicon}
                  alt="Location Icon"
                  style={{ width: '20px', height: '20x', marginRight: '5px' }}
                />
                {`${listingAddress.city}, ${listingAddress.state}`}
              </p>
            </div>
          ) : null}
          {sellerDetail ? null : hasBio ? (
            <ExpandableBio className={css.desktopBio} bio={bio} />
          ) : null}
        </div>
      </div>
      {!dubaiListing && <div className={css.linkContainer}>{links}</div>}
      {hasBio ? (
        <ExpandableBio className={css.mobileBio} bio={bio} dubaiListing={dubaiListing} />
      ) : null}
    </div>
  );
};

UserCard.defaultProps = {
  rootClassName: null,
  className: null,
  user: null,
  currentUser: null,
};

UserCard.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  currentUser: propTypes.currentUser,
  onContactUser: func.isRequired,
};

export default UserCard;
